import type { CSSProperties } from 'react';
import { GRADE_BLURBS } from '../../core/scoring';
import type { Grade } from '../../core/types';
import { useCountUp } from '../../hooks/useCountUp';
import { cn } from '../../lib/cn';
import { scoreTone } from '../../lib/tone';

export interface ScoreRingProps {
  score: number;
  grade: Grade;
  className?: string;
}

const SIZE = 176;
const STROKE = 12;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * The headline score, as a progress ring.
 *
 * Three accessibility details that are easy to get wrong on an SVG gauge:
 *
 *  1. The ring itself is `aria-hidden`, and the score is announced from the text
 *     beside it. Screen readers make nothing useful of a `<circle>`.
 *  2. That text sits in an `aria-live="polite"` region so the new score is read
 *     out as the user edits, without them having to go looking for it.
 *  3. The sweep is animated with `stroke-dashoffset`, which the global
 *     `prefers-reduced-motion` rule reduces to nothing for users who ask.
 *
 * The big number counts up to each new score rather than snapping. Note it is
 * `counted` that gets rendered visually and `clamped` that gets announced: the
 * live region must carry the settled figure, or a screen reader receives one
 * announcement per animation frame. `useCountUp` documents the rest.
 */
export function ScoreRing({ score, grade, className }: ScoreRingProps) {
  const clamped = Math.max(0, Math.min(100, score));
  const tone = scoreTone(clamped);
  const offset = CIRCUMFERENCE * (1 - clamped / 100);
  const counted = useCountUp(clamped);

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg
          aria-hidden="true"
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          // Start the sweep at 12 o'clock instead of 3.
          className="ring-glow -rotate-90"
          // `--ring-glow` feeds the `drop-shadow` in the `ring-glow` utility.
          // The cast is because React's CSSProperties has no index signature for
          // custom properties; the value is a plain string either way.
          style={{ '--ring-glow': tone.stroke } as CSSProperties}
        >
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="var(--ui-sunken)"
            strokeWidth={STROKE}
          />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={tone.stroke}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            style={{
              // Transitioning the offset means the ring sweeps to each new score
              // rather than jumping, which makes an improvement legible.
              transition: 'stroke-dashoffset 0.7s var(--ease-out-quint), stroke 0.4s linear',
            }}
          />
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {/* The one number on the page big enough to earn the display face.
              `global.css` sets it on h1–h4 and explains why body copy keeps the
              sans — Space Grotesk has personality at 30px and turns into a
              distraction at 14px. At 60px this is the far side of that line, and
              it is the single element someone remembers, so it gets the
              characterful figures. Every smaller score in the app — the section
              bars, the header chip — stays in the body face for the same reason. */}
          <span
            className={cn('font-display text-6xl font-bold tracking-tight tabular-nums', tone.text)}
          >
            {counted}
          </span>
          <span className="text-[0.65rem] font-semibold tracking-[0.12em] text-subtle uppercase">
            out of 100
          </span>
        </div>

        {/* The grade as a sticker. `aria-hidden` because the live region below
            already reads "Grade B · 72/100" — announcing the letter twice is
            noise, and this is decoration on top of information that is already
            there. */}
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-1/2 grid size-11 -translate-x-1/2 translate-y-1/4 place-items-center rounded-pill border-2 border-card text-lg font-bold text-white shadow-lifted"
          style={{ backgroundImage: 'var(--gradient-cta)' }}
        >
          {grade}
        </div>
      </div>

      <div aria-live="polite" className="mt-7 text-center">
        <p className="text-sm font-semibold text-strong">
          Grade {grade} · {clamped}/100
        </p>
        <p className="mt-1 max-w-64 text-sm text-muted">{GRADE_BLURBS[grade]}</p>
      </div>
    </div>
  );
}
